/* A decision walked branch by branch. Each pick prints its verdict and opens
   the next question, so the reasoning is the thing being read — not a score.
   Reaching a leaf completes the step. */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { treeComplete } from '../../engine/exercises';
import { useApp } from '../../state/AppProvider';
import type { Block, TreeBranchSpec, TreeSpec } from '../../types';

export function TreeBlock({ block }: { block: Block }) {
  const { t } = useTranslation();
  const tree = block.tree;
  if (!tree) return null;

  return (
    <figure className="fig" data-od-id="decision-tree">
      <div className="fig-head">
        <span className="gauge">
          {tree.branches.length} {t('branchesW')}
        </span>
        <span className="rule" />
      </div>
      <div className="fig-body">
        <div className="tree">
          <TreeLevel block={block} level={tree} depth={0} />
        </div>
      </div>
    </figure>
  );
}

function TreeLevel({ block, level, depth }: { block: Block; level: TreeSpec; depth: number }) {
  const { t } = useTranslation();
  const { progress, completeBlock } = useApp();
  const [path, setPath] = useState<number[]>([]);
  const chosen = path[depth];

  const pick = (index: number) => {
    const next = [...path.slice(0, depth), index];
    setPath(next);
    if (treeComplete(block, next) && !progress.done[block.id]) completeBlock(block.id);
  };

  return (
    <div className="tnode">
      <p className="tq">{t(level.qKey)}</p>
      <div className="tbranches">
        {level.branches.map((branch: TreeBranchSpec, index: number) => {
          const picked = chosen === index;
          return (
            <div key={branch.aKey}>
              <button
                type="button"
                className="tbranch"
                data-act="tree"
                data-id={block.id}
                data-d={depth}
                data-i={index}
                data-picked={picked}
                onClick={() => pick(index)}
              >
                <span className="bc">{picked ? '▾' : '▸'}</span>
                <span>{t(branch.aKey)}</span>
              </button>
              {picked && (
                <>
                  <Verdict branch={branch} />
                  {branch.then && <TreeLevel block={block} level={branch.then} depth={depth + 1} />}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Verdict({ branch }: { branch: TreeBranchSpec }) {
  const { t } = useTranslation();
  const word = branch.tone === 'stop' ? 'STOP' : branch.tone === 'pass' ? 'OK' : '?';
  return (
    <div className="verdict" data-tone={branch.tone}>
      <span className="vt">{word}</span>
      <span>{branch.verdictKey ? t(branch.verdictKey) : ''}</span>
    </div>
  );
}
